<template>
  <div id="pageUser">
    <v-container fluid>
      <v-row class="mb-4" v-if="!isAdmin">
        <v-col cols="12" sm="6" md="4">
          <v-card class="stat-card h-100">
            <v-card-text class="pa-4">
              <div class="d-flex align-center">
                <v-avatar color="primary lighten-4" size="52" class="mr-4">
                  <v-icon color="primary" size="28">mdi-account-group</v-icon>
                </v-avatar>
                <div>
                  <div class="text-h5 font-weight-bold">{{ totalItems }}</div>
                  <div class="text-subtitle-2 grey--text text--darken-1">Usuarios totales</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-card class="stat-card h-100">
            <v-card-text class="pa-4">
              <div class="d-flex align-center">
                <v-avatar color="green lighten-4" size="52" class="mr-4">
                  <v-icon color="green" size="28">mdi-account-check</v-icon>
                </v-avatar>
                <div>
                  <div class="text-h5 font-weight-bold">{{ userEnabled }}</div>
                  <div class="text-subtitle-2 grey--text text--darken-1">Usuarios habilitados</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-card class="stat-card h-100">
            <v-card-text class="pa-4">
              <div class="d-flex align-center">
                <v-avatar color="red lighten-4" size="52" class="mr-4">
                  <v-icon color="red" size="28">mdi-account-off</v-icon>
                </v-avatar>
                <div>
                  <div class="text-h5 font-weight-bold">{{ userDisabled }}</div>
                  <div class="text-subtitle-2 grey--text text--darken-1">Usuarios deshabilitados</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Vista principal -->
      <v-row>
        <v-col cols="12">
          <v-card class="user-card elevation-2">
            <!-- Header mejorado -->
            <v-toolbar color="primary" dark flat>
              <v-icon class="mr-2">mdi-account-supervisor</v-icon>
              <v-toolbar-title v-if="!$vuetify.breakpoint.mdAndDown">
                Lista de usuarios
              </v-toolbar-title>
              <v-spacer></v-spacer>

              <!-- Búsqueda para escritorio -->
              <v-col cols="3" class="mr-3" v-if="!$vuetify.breakpoint.mdAndDown">
                <v-text-field
                  v-model="filter.search"
                  label="Buscar usuario..."
                  prepend-inner-icon="mdi-magnify"
                  hide-details
                  clearable
                  rounded
                  single-line
                  filled
                  dense
                  dark
                  @input="debouncedLoadData"
                ></v-text-field>
              </v-col>

              <!-- Búsqueda para móvil -->
              <v-tooltip top v-if="$vuetify.breakpoint.mdAndDown && !showSearch">
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" @click.stop="toggleSearch">
                    <v-icon>mdi-magnify</v-icon>
                  </v-btn>
                </template>
                <span>Buscar</span>
              </v-tooltip>

              <v-col cols="12" v-if="showSearch && $vuetify.breakpoint.mdAndDown">
                <v-text-field
                  ref="searchInput"
                  v-model="filter.search"
                  label="Buscar usuario..."
                  prepend-inner-icon="mdi-magnify"
                  hide-details
                  clearable
                  rounded
                  single-line
                  filled
                  dense
                  dark
                  @keydown.esc="showSearch = false"
                  @input="debouncedLoadData"
                ></v-text-field>
              </v-col>

              <!-- Menú de filtros -->
              <filter-menu
                title="Filtrar usuarios"
                :current-filter="currentFilter"
                @canceled="rollbackFilter()"
                @filtered="commitFilter()"
              >
                <v-row>
                  <template v-if="isAdmin">
                    <v-col cols="12">
                      <v-autocomplete v-model="filter.grupoId" :items="grupos" item-text="name" item-value="id"
                        label="Grupo" outlined clearable hide-details="auto" prepend-inner-icon="mdi-account-group"></v-autocomplete>
                    </v-col>
                    <v-col cols="12">
                      <v-autocomplete v-model="filter.dealerId" :items="filterDealers" item-text="name" item-value="id"
                        label="Dealer" outlined clearable hide-details="auto" prepend-inner-icon="mdi-store"></v-autocomplete>
                    </v-col>
                  </template>
                  <v-col cols="12">
                    <v-select
                      v-model="filter.roles"
                      :items="filterRoles"
                      label="Rol"
                      outlined
                      clearable
                      hide-details="auto"
                      class="mb-4"
                      prepend-inner-icon="mdi-shield-account"
                    >
                      <template v-slot:selection="{ item }">
                        <v-icon left small>mdi-shield-account</v-icon>
                        {{ getRoleName(item) }}
                      </template>
                      <template v-slot:item="{ item }">
                        <v-list-item-icon class="mr-2">
                          <v-icon>mdi-shield-account</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>{{ getRoleName(item) }}</v-list-item-title>
                        </v-list-item-content>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12">
                    <v-select
                      v-model="filter.status"
                      :items="estados"
                      item-text="name"
                      item-value="value"
                      label="Estado"
                      outlined
                      clearable
                      hide-details="auto"
                      prepend-inner-icon="mdi-checkbox-marked-circle-outline"
                    >
                      <template v-slot:selection="{ item }">
                        <v-icon left small :color="item.value === 10 ? 'green' : 'red'">
                          {{ item.value === 10 ? 'mdi-account-check' : 'mdi-account-off' }}
                        </v-icon>
                        {{ item.name }}
                      </template>
                      <template v-slot:item="{ item }">
                        <v-icon left :color="item.value === 10 ? 'green' : 'red'">
                          {{ item.value === 10 ? 'mdi-account-check' : 'mdi-account-off' }}
                        </v-icon>
                        {{ item.name }}
                      </template>
                    </v-select>
                  </v-col>
                </v-row>
              </filter-menu>

              <!-- Botón agregar usuario -->
              <v-tooltip top v-if="canAddUser">
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" @click="showUserForm(null)" class="mr-2" color="white">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>Agregar usuario</span>
              </v-tooltip>

              <!-- Botón actualizar -->
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn icon @click="refreshDataTable" v-on="on" class="mr-2" color="white" :disabled="loadingItems">
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>Actualizar lista</span>
              </v-tooltip>
            </v-toolbar>

            <!-- Formulario modal -->
            <user-form
              v-if="visibleForm"
              v-model="editItem"
              @save="updateDataTable"
              @cancel="visibleForm = false"
            ></user-form>

            <!-- Filtros rápidos bajo la barra de herramientas -->
            <v-card-text class="py-2 px-4">
              <v-row class="align-center justify-center">
                <v-col cols="auto">
                  <v-chip-group v-model="quickFilter" @change="applyQuickFilter" class="py-1">
                    <v-chip filter outlined color="green" small class="mr-1">
                      <v-icon left x-small>mdi-account-check</v-icon>
                      Habilitados
                    </v-chip>
                    <v-chip filter outlined color="red" small class="mr-1">
                      <v-icon left x-small>mdi-account-off</v-icon>
                      Deshabilitados
                    </v-chip>
                  </v-chip-group>
                </v-col>

                <v-col cols="auto" v-if="hasActiveFilters">
                  <v-btn x-small text color="primary" @click="resetFilters" class="ml-2">
                    <v-icon left small>mdi-filter-remove</v-icon>
                    Limpiar filtros
                  </v-btn>
                </v-col>

                <v-col cols="auto" class="ml-auto">
                  <v-btn-toggle v-model="viewMode" mandatory dense color="primary">
                    <v-btn value="cards" small>
                      <v-icon small>mdi-view-grid-outline</v-icon>
                      Tarjetas
                    </v-btn>
                    <v-btn value="table" small>
                      <v-icon small>mdi-table</v-icon>
                      Tabla
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>

              <!-- Filtros aplicados -->
              <v-row v-if="hasActiveFilters" class="mt-1 justify-center">
                <v-col cols="12">
                  <div class="d-flex align-center flex-wrap justify-center">
                    <span class="text-caption mr-2 grey--text">Filtros aplicados:</span>
                    <v-chip v-if="filter.grupoId" x-small class="mr-1 mb-1" close color="primary" text-color="white"
                      @click:close="filter.grupoId = null; commitFilter()">
                      Grupo: {{ grupoName(filter.grupoId) }}
                    </v-chip>
                    <v-chip v-if="filter.dealerId" x-small class="mr-1 mb-1" close color="primary" text-color="white"
                      @click:close="filter.dealerId = null; commitFilter()">
                      Dealer: {{ dealerName(filter.dealerId) }}
                    </v-chip>
                    <v-chip v-if="filter.roles" x-small class="mr-1 mb-1" close color="primary" text-color="white"
                      @click:close="filter.roles = null; commitFilter()">
                      Rol: {{ getRoleName(filter.roles) }}
                    </v-chip>
                    <v-chip v-if="filter.status !== null" x-small class="mr-1 mb-1" close
                      :color="filter.status === 10 ? 'green' : 'red'" text-color="white"
                      @click:close="filter.status = null; commitFilter()">
                      {{ filter.status === 10 ? 'Habilitado' : 'Deshabilitado' }}
                    </v-chip>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>

            <v-divider></v-divider>

            <!-- Estado de carga -->
            <div v-if="loadingItems" class="pa-3">
              <!-- Skeleton loaders para la vista de tabla -->
              <template v-if="viewMode === 'table'">
                <v-skeleton-loader type="table-heading" class="mb-2"></v-skeleton-loader>
                <v-skeleton-loader type="table-row@6"></v-skeleton-loader>
              </template>

              <!-- Skeleton loaders para vista de tarjetas -->
              <v-row v-else>
                <v-col cols="12" sm="6" md="4" lg="3" v-for="n in 8" :key="`skeleton-${n}`">
                  <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
                </v-col>
              </v-row>
            </div>

            <!-- Vista de tabla -->
            <v-card-text v-else-if="viewMode === 'table'" class="pa-0">
              <v-data-table
                :headers="headers"
                :items="USERS"
                :options.sync="pagination"
                :server-items-length="totalItems"
                :loading="loadingItems"
                :footer-props="footer"
                :key="tableId"
                class="elevation-0"
                multi-sort
              >
                <!-- Columna de nombre -->
                <template v-slot:item.name="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar color="primary lighten-4" size="36" class="mr-3">
                      <v-icon size="18" color="primary">mdi-account</v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium">{{ item.name }} {{ item.lastname }}</div>
                      <div class="caption grey--text text--darken-1">{{ item.email }}</div>
                    </div>
                  </div>
                </template>

                <!-- Columna de teléfono -->
                <template v-slot:item.phone="{ item }">
                  <div class="d-flex align-center">
                    <v-icon small color="grey darken-1" class="mr-1">mdi-phone</v-icon>
                    {{ item.phone || 'No disponible' }}
                  </div>
                </template>

                <!-- Columnas de grupo y dealer -->
                <template v-slot:item.grupo="{ item }">
                  <span v-if="item.grupo">{{ item.grupo.name }}</span>
                  <span v-else class="grey--text">—</span>
                </template>
                <template v-slot:item.dealer="{ item }">
                  <span v-if="item.dealer">{{ item.dealer.name }}</span>
                  <span v-else class="grey--text">—</span>
                </template>

                <!-- Columna de roles -->
                <template v-slot:item.roles="{ item }">
                  <div>
                    <v-chip v-for="(rol, index) in item.roles" :key="index"
                      small outlined class="mr-1 mb-1" color="primary">
                      {{ getRoleName(rol) }}
                    </v-chip>
                    <v-chip v-if="!isAdmin && item.permissions_customized" x-small color="orange" dark class="mb-1"
                      title="El dealer ha modificado los permisos predefinidos de su rol">
                      Permisos modificados
                    </v-chip>
                  </div>
                </template>

                <!-- Columna de estado -->
                <template v-slot:item.status="{ item }">
                  <v-chip :color="getColor(item.status)" small dark>
                    <v-icon left x-small>
                      {{ item.status == 10 ? "mdi-check-circle" : "mdi-block-helper" }}
                    </v-icon>
                    {{ item.status == 10 ? "Habilitado" : "Deshabilitado" }}
                  </v-chip>
                </template>

                <!-- Columna de acciones -->
                <template v-slot:item.action="{ item }">
                  <div v-if="!loadingItems && item.username !== currentUser" class="d-inline-flex action-buttons">
                    <!-- Botón Editar -->
                    <v-tooltip top v-if="canUpdateUser && item.email !== currentUserEmail">
                      <template v-slot:activator="{ on }">
                        <v-btn icon small color="primary" @click="showUserForm(item)" v-on="on">
                          <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>Editar</span>
                    </v-tooltip>

                    <!-- Botón Deshabilitar -->
                    <v-tooltip top v-if="canDeleteUser && item.email !== currentUserEmail">
                      <template v-slot:activator="{ on }">
                        <v-btn v-show="item.status === 10" icon small color="red" @click="disableUser(item)" v-on="on">
                          <v-icon>mdi-account-off</v-icon>
                        </v-btn>
                      </template>
                      <span>Deshabilitar</span>
                    </v-tooltip>

                    <!-- Botón Habilitar -->
                    <v-tooltip top v-if="canDeleteUser">
                      <template v-slot:activator="{ on }">
                        <v-btn v-show="item.status !== 10" icon small color="green" @click="enableUser(item)" v-on="on">
                          <v-icon>mdi-account-check</v-icon>
                        </v-btn>
                      </template>
                      <span>Habilitar</span>
                    </v-tooltip>
                  </div>
                </template>

                <!-- Sin resultados -->
                <template v-slot:no-data>
                  <div class="text-center pa-6">
                    <h3 class="text-h5 grey--text text--darken-1 mb-2">No se encontraron usuarios</h3>
                    <p class="subtitle-1 grey--text mb-4">
                      Intenta con otros filtros o agrega un nuevo usuario
                    </p>
                    <v-btn v-if="canAddUser" color="primary" outlined @click="showUserForm(null)">
                      <v-icon left>mdi-plus</v-icon>
                      Agregar usuario
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Vista de tarjetas -->
            <v-card-text v-else class="pa-3">
              <v-row>
                <v-col cols="12" sm="6" md="4" lg="3" v-for="item in USERS" :key="item.id">
                  <v-card outlined hover class="user-item-card h-100">
                    <v-card-text class="pa-4">
                      <!-- Header de tarjeta con estado -->
                      <div class="d-flex justify-space-between align-center mb-3">
                        <v-chip :color="getColor(item.status)" small dark>
                          <v-icon left x-small>
                            {{ item.status == 10 ? "mdi-check-circle" : "mdi-block-helper" }}
                          </v-icon>
                          {{ item.status == 10 ? "Habilitado" : "Deshabilitado" }}
                        </v-chip>
                        <v-menu bottom left offset-y transition="slide-y-transition" v-if="item.email !== currentUserEmail">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn icon x-small v-bind="attrs" v-on="on">
                              <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                          </template>
                          <v-list dense>
                            <v-list-item v-if="canUpdateUser" @click="showUserForm(item)">
                              <v-list-item-icon class="mr-2">
                                <v-icon small>mdi-pencil</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title>Editar</v-list-item-title>
                            </v-list-item>
                            <v-list-item v-if="canDeleteUser" @click="disableUser(item)" v-show="item.status === 10">
                              <v-list-item-icon class="mr-2">
                                <v-icon small color="red">mdi-account-off</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title class="red--text">Deshabilitar</v-list-item-title>
                            </v-list-item>
                            <v-list-item v-if="canDeleteUser" @click="enableUser(item)" v-show="item.status !== 10">
                              <v-list-item-icon class="mr-2">
                                <v-icon small color="green">mdi-account-check</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title class="green--text">Habilitar</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>

                      <!-- Avatar y detalles principales -->
                      <div class="d-flex mb-4">
                        <v-avatar size="56" :color="`${getColor(item.status)} lighten-4`" class="mr-3">
                          <v-icon size="32" :color="getColor(item.status)">mdi-account</v-icon>
                        </v-avatar>
                        <div>
                          <div class="subtitle-1 font-weight-medium mb-1">
                            {{ item.name }} {{ item.lastname }}
                          </div>
                          <div class="caption grey--text text--darken-1">
                            {{ item.username }}
                          </div>
                        </div>
                      </div>

                      <!-- Información básica -->
                      <v-list dense class="pa-0 transparent">
                        <v-list-item class="px-0">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="grey darken-1">mdi-email</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>{{ item.email || 'Sin email' }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item class="px-0">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="grey darken-1">mdi-phone</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>{{ item.phone || 'Sin teléfono' }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item class="px-0">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="grey darken-1">mdi-shield-account</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>
                              <div>
                                <v-chip v-for="(rol, index) in item.roles" :key="index"
                                  x-small outlined class="mr-1 mb-1" color="primary">
                                  {{ getRoleName(rol) }}
                                </v-chip>
                              </div>
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Sin resultados -->
              <v-row v-if="!USERS.length && !loadingItems">
                <v-col cols="12" class="text-center pa-6">
                  <h3 class="text-h5 grey--text text--darken-1 mb-2">No se encontraron usuarios</h3>
                  <p class="subtitle-1 grey--text mb-4">
                    Intenta con otros filtros o agrega un nuevo usuario
                  </p>
                  <v-btn v-if="canAddUser" color="primary" outlined @click="showUserForm(null)">
                    <v-icon left>mdi-plus</v-icon>
                    Agregar usuario
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Paginación en vista de tarjetas -->
              <div class="text-center pt-4" v-if="!loadingItems && USERS.length > 0">
                <v-row>
                  <v-col cols="12" class="d-flex justify-center align-center flex-wrap pa-2">
                    <div class="text-center d-flex align-center mr-4">
                      <span class="text-body-2 grey--text text--darken-1">Filas por página:</span>
                      <v-select v-model="pagination.itemsPerPage" :items="footer.itemsPerPageOptions" dense outlined
                        hide-details class="ml-2" style="width: 80px;" @change="onItemsPerPageChange"></v-select>
                    </div>

                    <v-pagination v-model="pagination.page" :length="Math.ceil(totalItems / pagination.itemsPerPage)"
                      :total-visible="7" circle @input="onPageChange"></v-pagination>

                    <span class="text-body-2 grey--text text--darken-1 ml-4">
                      {{ paginationText }}
                    </span>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import FilterMenu from '../../components/FilterMenu'
import UserService from '@/services/UserService'
import GrupoService from '@/services/GrupoService'
import DealerService from '@/services/DealerService'

const STATUS_DISABLED = 9
const STATUS_ENABLED = 10

export default {
  components: {
    FilterMenu,
    UserForm: () => import(/* webpackPrefetch: true */ './UserForm')
  },

  data: () => ({
    // Vista y carga
    loadingItems: false,
    totalItems: 0,
    tableId: 0,
    showSearch: false,
    quickFilter: null,
    userEnabled: 0,
    userDisabled: 0,
    
    manualViewMode: null,

    // datatable
    headers: [
      { text: 'Usuario', value: 'username' },
      { text: 'Nombre y apellidos', value: 'name', sortable: false },
      { text: 'Teléfono', value: 'phone' },
      { text: 'Rol', value: 'roles' },
      { text: 'Estado', value: 'status', align: 'center' },
      { text: 'Acciones', value: 'action', sortable: false, align: 'center', width: '120px' }
    ],
    footer: {
      itemsPerPageOptions: [10, 20, 30, 40, 50],
      itemsPerPageText: 'Usuarios por página:',
      pageText: '{0}-{1} de {2}'
    },
    // Mismos nombres que en el formulario de usuario y en Permisos del Administrador de Grupo
    roleDisplay: {
      admin: 'Administrador',
      group_admin: 'Administrador de Grupo',
      manager: 'Manager',
      logistics: 'Logística',
      sales: 'Jefe de ventas/taller',
      garage: 'Jefe de taller',
      commercial: 'Comercial',
      mechanic: 'Mecánico',
      cleaning: 'Limpieza',
      driver: 'Conductor',
      colaborator: 'Colaborador'
    },

    // filtro
    filter: {
      search: null,
      grupoId: null,
      dealerId: null,
      roles: null,
      status: STATUS_ENABLED
    },
    currentFilter: {
      grupoId: null,
      dealerId: null,
      roles: null,
      status: STATUS_ENABLED
    },
    grupos: [],
    dealers: [],
    estados: [
      { name: 'Habilitado', value: STATUS_ENABLED },
      { name: 'Deshabilitado', value: STATUS_DISABLED }
    ],

    currentUserEmail: null,

    // form
    visibleForm: false,
    editItem: null
  }),

  async created () {
    // cargando roles desde el backend
    this.currentUserEmail = JSON.parse(localStorage.getItem('username')).email
    await this.$store.dispatch('LOAD_ROLES')
    if (this.isAdmin) {
      this.loadGruposAndDealers()
      // Entorno Grupo: columnas de grupo y dealer para el Super Admin
      this.headers.splice(3, 0, { text: 'Grupo', value: 'grupo', sortable: false }, { text: 'Dealer', value: 'dealer', sortable: false })
    }
    // Bandera para evitar recarga durante la inicialización
    this.isInitializing = true
    this.loadData()
    // Desactivar la bandera de inicialización después de la carga inicial
    this.$nextTick(() => {
      this.isInitializing = false
    })
  },

  methods: {
    loadData () {
      this.loadingItems = true
       const filterWithTypes = {
        ...this.filter
      }
      this.$store.dispatch('LOAD_USERS', filterWithTypes).then(result => {
        // Solo actualizar totalItems si NO hay búsqueda (para evitar parpadeo)
        if (!this.filter.search || this.filter.search.trim() === '') {
          this.totalItems = result || 0
        }
        const maxPage = Math.ceil(result / this.pagination.itemsPerPage)
        if (this.pagination.page > maxPage && maxPage > 0) {
          this.pagination.page = maxPage
        }
        this.loadingItems = false
        // Cargar estadísticas con los filtros actuales cuando no es admin
        if (!this.isAdmin) {
          this.loadStats(this.buildFilterParams())
        }
      }).catch(error => {
        console.error('Error al cargar usuarios:', error)
        this.$toast.error('Error al cargar los usuarios')
        this.loadingItems = false
      })
    },
    async loadGruposAndDealers () {
      try {
        const [grupos, dealers] = await Promise.all([GrupoService.index({ 'per-page': 100 }), DealerService.get({ 'per-page': 100 })])
        this.grupos = grupos.data.data || []
        this.dealers = (dealers.data.data || []).filter(d => !d.is_colaborador)
      } catch (error) {
        console.error('Error al cargar grupos y dealers:', error)
      }
    },
    grupoName (id) {
      const grupo = this.grupos.find(g => g.id === id)
      return grupo ? grupo.name : id
    },
    dealerName (id) {
      const dealer = this.dealers.find(d => d.id === id)
      return dealer ? dealer.name : id
    },
    debouncedLoadData: _.debounce(function () {
      this.loadData()
    }, 500),
    async loadStats(filterParams = null) {
      try {
        this.loadingStats = true
        // Si no se pasan parámetros de filtro, usar los filtros actuales
        const statsParams = filterParams || this.buildFilterParams()
        
        // Convertir los parámetros al formato que espera el backend (filter[key])
        const formattedParams = {}
        for (let key in statsParams) {
          if (statsParams.hasOwnProperty(key)) {
            formattedParams[`filter[${key}]`] = statsParams[key]
          }
        }
        
        const stats = await UserService.getStats(formattedParams)

        // Actualizar totalItems con las estadísticas (siempre, con o sin búsqueda)
        this.totalItems = stats.data.data.active + stats.data.data.inactive || 0
        this.userEnabled = stats.data.data.active || 0
        this.userDisabled = stats.data.data.inactive || 0
        
        // Forzar actualización de la UI
        this.$nextTick(() => {
          this.$forceUpdate()
        })
      } catch (error) {
        console.error('Error al cargar estadísticas:', error)
        this.$toast.error('Error al cargar las estadísticas de usuarios')
      } finally {
        this.loadingStats = false
      }
    },
    buildFilterParams() {
      // Construir parámetros de filtro
      let filterParams = { ...this.filter }
      
      // Limpiar valores nulos o indefinidos
      Object.keys(filterParams).forEach(key => {
        if (filterParams[key] === null || filterParams[key] === undefined || filterParams[key] === '') {
          delete filterParams[key]
        }
      })
      
      return filterParams
    },
    updateDataTable ({ insert }) {
      this.visibleForm = false
      if (insert) {
        this.totalItems++
      }
      this.refreshDataTable()
      this.$toast.success(insert ? 'Usuario creado con éxito' : 'Usuario actualizado con éxito')
    },
    getColor (estado) {
      return (estado === STATUS_ENABLED ? `green` : `red`)
    },
    refreshDataTable () {
      this.tableId++
      this.loadData()
      if (!this.isAdmin) {
        this.loadStats(this.buildFilterParams())
      }
    },
    // Método para obtener el nombre de visualización del rol
    getRoleName (rol) {
      return this.roleDisplay[rol] || rol.charAt(0).toUpperCase() + rol.slice(1)
    },

    // Manejo de búsqueda en móvil
    toggleSearch () {
      this.showSearch = !this.showSearch
      if (this.showSearch) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus()
          }
        })
      }
    },
    handleClickOutside (event) {
      const searchInput = this.$refs.searchInput
      if (searchInput && !searchInput.$el.contains(event.target)) {
        this.showSearch = false
      }
    },

    // filtros
    commitFilter () {
      for (const key in this.currentFilter) {
        this.currentFilter[key] = this.filter[key]
      }
      this.loadData()
      if (!this.isAdmin) {
        this.loadStats(this.buildFilterParams())
      }
    },
    rollbackFilter () {
      for (const key in this.currentFilter) {
        this.filter[key] = this.currentFilter[key]
      }
      if (!this.isAdmin) {
        this.loadStats(this.buildFilterParams())
      }
    },
    resetFilters () {
      for (const key in this.filter) {
        this.filter[key] = null
      }
      this.quickFilter = null
      this.commitFilter()
    },
    applyQuickFilter (index) {
      if (index === null) {
        this.filter.status = null
      } else {
        const statusValues = [STATUS_ENABLED, STATUS_DISABLED]
        this.filter.status = statusValues[index]
      }
      this.commitFilter()
    },

    // acciones
    showUserForm (item) {
      this.editItem = item ? { ...item } : null
      this.visibleForm = true
    },
    enableUser (item) {
      this.$confirm('¿Está seguro que desea habilitar el usuario?').then(async result => {
        if (result) {
          this.loadingItems = true
          item.status = STATUS_ENABLED
          this.$store.dispatch('ENABLE_USER', item).then(() => {
            this.loadingItems = false
            this.$toast.success('Usuario habilitado con éxito')
            this.refreshDataTable()
          }).catch(error => {
            console.error('Error al habilitar usuario:', error)
            this.$toast.error('Error al habilitar el usuario')
            this.loadingItems = false
          })
        }
      })
    },
    disableUser (item) {
      this.$confirm('¿Está seguro que desea deshabilitar el usuario?').then(async result => {
        if (result) {
          this.loadingItems = true
          item.status = STATUS_DISABLED
          this.$store.dispatch('DELETE_USER', item).then(() => {
            this.loadingItems = false
            this.$toast.success('Usuario deshabilitado con éxito')
            this.refreshDataTable()
          }).catch(error => {
            console.error('Error al deshabilitar usuario:', error)
            this.$toast.error('Error al deshabilitar el usuario')
            this.loadingItems = false
          })
        }
      })
    },
    onPageChange (page) {
      // Actualizar la página en la paginación
      this.pagination.page = page
      // Forzar la carga de datos inmediatamente después del cambio de página
      this.loadData()
    },
    onItemsPerPageChange (perPage) {
      // Actualizar items por página en la paginación
      this.pagination.itemsPerPage = perPage
      // Asegurarse de volver a la primera página al cambiar elementos por página
      this.pagination.page = 1
      // Forzar la carga de datos
      this.loadData()
    }
  },

  computed: {
    ...mapGetters(['USERS', 'ROLES', 'CURRENT_USER', 'CURRENT_USER_ROLES', 'HAS_PERMISSION']),
    // Roles que se pueden elegir en el filtro: los que tienen nombre en la app (sin driver/cleaning, desactivados);
    // los de sistema (administración y colaborador) solo los ve el Super Admin
    filterRoles () {
      const systemRoles = ['admin', 'group_admin', 'colaborator']
      const order = Object.keys(this.roleDisplay)
      return (this.ROLES || [])
        .filter(role => this.roleDisplay[role] && (this.isAdmin || !systemRoles.includes(role)))
        .sort((a, b) => order.indexOf(a) - order.indexOf(b))
    },
    currentUser () {
      return this.CURRENT_USER.username
    },

    // Permisos de usuario — admin puede hacerlo todo
    canManageUsers() {
      return this.isAdmin || this.HAS_PERMISSION('manage_users')
    },

    canAddNormalUser() {
      return this.isAdmin || this.HAS_PERMISSION('agregar_usuario_normal')
    },

    canUpdateUser() {
      return this.isAdmin || this.HAS_PERMISSION('actualizar_usuario')
    },

    canDeleteUser() {
      return this.isAdmin || this.HAS_PERMISSION('eliminar_usuario')
    },

    // Verificar si puede agregar usuarios (manage_users o agregar_usuario_normal)
    canAddUser() {
      return this.isAdmin || this.canManageUsers || this.canAddNormalUser
    },
    viewMode: {
      get() {
        // Si el usuario cambió manualmente la vista, usar esa
        if (this.manualViewMode) {
          return this.manualViewMode
        }
        // Detectar dispositivo y retornar vista por defecto
        if (this.$vuetify && this.$vuetify.breakpoint && this.$vuetify.breakpoint.mdAndUp) {
          return 'table'
        }
        return 'cards'
      },
      set(value) {
        // Permitir cambio manual de vista
        this.manualViewMode = value
      }
    },
    pagination: {
      get: function () {
        return this.$store.getters.USERS_PAG
      },
      set: function (value) {
        this.$store.commit('SET_USERS_PAG', value)
      }
    },
    // Dealers del grupo elegido en el filtro (o todos)
    filterDealers () {
      return this.filter.grupoId ? this.dealers.filter(d => d.grupo_id === this.filter.grupoId) : this.dealers
    },
    isAdmin () {
      // Aceptar tanto clave 'admin' como la etiqueta localizada 'Administrador'
      return this.CURRENT_USER_ROLES.includes('admin') || this.CURRENT_USER_ROLES.includes('Administrador')
    },
    paginationText () {
      const start = (this.pagination.page - 1) * this.pagination.itemsPerPage + 1
      const end = Math.min(start + this.pagination.itemsPerPage - 1, this.totalItems)
      return `${start}-${end} de ${this.totalItems}`
    },
    searchTerm () {
      return this.filter.search
    },
    hasActiveFilters () {
      return Object.values(this.filter).some(value =>
        value !== null && value !== undefined && value !== ''
      )
    }
  },

  watch: {
    pagination: {
      handler (newVal, oldVal) {
        // Verificar si realmente cambió algo importante que requiera recargar
        const relevantPropsChanged = ['page', 'itemsPerPage', 'sortBy', 'sortDesc'].some(
          prop => !_.isEqual(newVal[prop], oldVal?.[prop])
        )
        // Solo recargar si hay cambios relevantes y no estamos en medio de una carga
        if (relevantPropsChanged && !this.loadingItems && !this.isInitializing) {
          this.loadData()
        }
      },
      deep: true
    },
    searchTerm () {
      this.debouncedLoadData()
    },
    viewMode (newVal, oldVal) {
      console.log(`Vista cambiada de ${oldVal} a ${newVal}`)
    },
    manualViewMode (newVal, oldVal) {
      // Incrementar tableId cuando cambiamos a vista de tabla para forzar re-renderizado
      if (newVal === 'table' && oldVal === 'cards') {
        this.$nextTick(() => {
          this.tableId++
          setTimeout(() => {}, 100)
        })
      }
    }
  }
}
</script>

<style scoped>
.user-card {
  border-radius: 8px;
  overflow: hidden;
}

.stat-card {
  border-radius: 8px;
  transition: all 0.3s;
}

.stat-card:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08) !important;
  transform: translateY(-3px);
}

.user-item-card {
  transition: box-shadow 0.2s ease-in-out, transform 0.2s ease;
  border-radius: 8px;
}

.user-item-card:hover {
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1) !important;
  transform: translateY(-2px);
}

.action-buttons button {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.action-buttons button:hover {
  opacity: 1;
}

/* Animación para nuevos elementos */
@keyframes highlight {
  0% {
    background-color: rgba(var(--v-primary-base), 0.1);
  }
  100% {
    background-color: transparent;
  }
}

.new-item {
  animation: highlight 2s ease-in-out;
}

.search-input-mobile {
  max-width: 200px;
}
</style>
